import React, { useState, useEffect } from 'react';
import { Modal, Spinner, Card, Toast, ToastContainer } from 'react-bootstrap';
import { Capacitor } from '@capacitor/core';
import QRScanSection from './QRScanSection';
import FaceScanHeroPanel from './FaceScanHeroPanel';
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG';
import { useAuth } from '../../auth';
import UserDataForm from '../../facescan/components/UserDataForm';
import FaceScanIframe from '../../facescan/components/FaceScanIframe';
import ScanResults from '../../facescan/components/ScanResults';
import {
  UserDemographics,
  FaceScanModuleData,
  SkinScanData,
  FaceScanAPIRequest,
  SkinScanAPIRequest,
  ScanStep,
} from '../../facescan/types';
import './FaceScanInfoModal.css';
import './FaceScanHeroPanel.css';

type Props = {
  show: boolean;
  handleClose: () => void;
};

// API Configuration (same as standalone page)
const FACESCAN_API_URL = 'https://prd.server.watchyourhealth.com/apis/s/face-vital-scan';
const SKINSCAN_API_URL = 'https://prd.server.watchyourhealth.com/apis/s/skin-scan';
const API_KEY = 'ra-CgCkaCR2gX4EeNRVarnVz5kVvNWqN05f';
const LOGGING_WEBHOOK_URL = 'https://etl.futurisk.in/webhook/f3313cc4-3649-4a4b-a9e4-eb28c7581d5c';

const createScanSessionUid = () => `modal_${Date.now()}`;

const isNativeMobile = (): boolean => {
  return Capacitor.isNativePlatform();
};

const FaceScanInfoModal: React.FC<Props> = ({ show, handleClose }) => {
  const { currentUser } = useAuth();
  const crmClientId = currentUser?.CRMClientId || '';
  const faceScanUrl = `https://plus.futurisk.in/face-scan?crmclientid=${encodeURIComponent(crmClientId)}`;
  const isMobile = isNativeMobile();

  // Scan flow state
  const [currentStep, setCurrentStep] = useState<ScanStep>(ScanStep.USER_INFO);
  const [userData, setUserData] = useState<UserDemographics | null>(null);
  const [faceScanData, setFaceScanData] = useState<FaceScanModuleData | null>(null);
  const [skinScanData, setSkinScanData] = useState<SkinScanData | null>(null);
  const [scanResults, setScanResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [scanUid, setScanUid] = useState<string>(() => createScanSessionUid());

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      // Delay reset to allow close animation
      const timer = setTimeout(() => {
        setCurrentStep(ScanStep.USER_INFO);
        setUserData(null);
        setFaceScanData(null);
        setSkinScanData(null);
        setScanResults(null);
        setError(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [show]);

  const showMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleUserDataSubmit = (data: UserDemographics) => {
    setScanUid(createScanSessionUid());
    setUserData(data);
    setCurrentStep(ScanStep.SCANNING);
  };

  const handleScanComplete = (scanData: FaceScanModuleData) => {
    console.log('=== MODAL SCAN COMPLETE ===');
    setFaceScanData(scanData);
  };

  const handleSkinScanComplete = (skinData: SkinScanData) => {
    console.log('=== MODAL SKIN SCAN DATA ===');
    setSkinScanData(skinData);
  };

  // Submit data when face scan completes (same logic as standalone page)
  useEffect(() => {
    const submitData = async () => {
      if (!userData || !faceScanData) return;

      setCurrentStep(ScanStep.PROCESSING);

      try {
        const faceScanRequest: FaceScanAPIRequest = {
          ...faceScanData,
          ...userData,
          uid: scanUid,
          org_name: 'futurisk_wellness',
          user_name: userData.name,
        };

        const faceScanResponse = await fetch(FACESCAN_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
          },
          body: JSON.stringify(faceScanRequest),
        });

        if (!faceScanResponse.ok) {
          const errorText = await faceScanResponse.text();
          throw new Error(`Face scan API failed (${faceScanResponse.status}): ${errorText}`);
        }

        const responseText = await faceScanResponse.text();
        let faceScanResult;
        try {
          faceScanResult = JSON.parse(responseText);
        } catch {
          throw new Error('Invalid response format from API');
        }

        // Skin scan submission
        let skinScanResult = null;
        if (skinScanData) {
          const skinScanRequest: SkinScanAPIRequest = {
            image_base64: skinScanData.image,
            org_name: 'futurisk_wellness',
          };

          const skinScanResponse = await fetch(SKINSCAN_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'x-api-key': API_KEY,
            },
            body: JSON.stringify(skinScanRequest),
          });

          if (skinScanResponse.ok) {
            const skinResponseText = await skinScanResponse.text();
            try {
              skinScanResult = JSON.parse(skinResponseText);
            } catch {
              console.error('Failed to parse skin scan response');
            }
          }
        }

        const combinedResults = {
          ...faceScanResult,
          skinScan: skinScanResult,
        };

        // Log to webhook (non-blocking)
        try {
          await fetch(LOGGING_WEBHOOK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userData: { ...userData, crmClientId },
              faceScanResult,
              skinScanResult,
              timestamp: new Date().toISOString(),
              scanUid,
            }),
          });
        } catch {
          console.warn('Webhook logging failed (non-critical)');
        }

        setScanResults(combinedResults);
        setCurrentStep(ScanStep.RESULTS);
        showMessage('Scan completed successfully!');
      } catch (err: any) {
        console.error('Scan submission error:', err);
        setError(err.message || 'Failed to process scan data');
        setCurrentStep(ScanStep.ERROR);
        showMessage('Failed to process scan data. Please try again.');
      }
    };

    if (faceScanData && userData && currentStep === ScanStep.SCANNING) {
      submitData();
    }
  }, [faceScanData, userData, currentStep, skinScanData, scanUid, crmClientId]);

  const handleScanError = (errorMsg: string) => {
    setError(errorMsg);
    setCurrentStep(ScanStep.ERROR);
    showMessage(errorMsg);
  };

  const handleNewScan = () => {
    setCurrentStep(ScanStep.USER_INFO);
    setUserData(null);
    setFaceScanData(null);
    setSkinScanData(null);
    setScanResults(null);
    setError(null);
  };

  const handleCancelScan = () => {
    setCurrentStep(ScanStep.USER_INFO);
    setFaceScanData(null);
    setSkinScanData(null);
  };

  const handleDownloadReport = () => {
    if (!scanResults) return;
    const dataStr = JSON.stringify(scanResults, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `face-scan-report-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showMessage('Report downloaded successfully!');
  };

  // Whether the scan flow is active (past the initial form)
  const isScanActive = currentStep !== ScanStep.USER_INFO;

  // Determine if modal should be fullscreen when scan is active
  const modalSize = isScanActive ? undefined : 'xl';
  const modalDialogClass = isScanActive
    ? 'facescan-info-modal facescan-modal-fullwidth'
    : 'facescan-info-modal';

  // ---------- Scan Flow Content (shared between mobile & desktop) ----------
  const renderScanFlow = () => {
    switch (currentStep) {
      case ScanStep.USER_INFO:
        return <UserDataForm onSubmit={handleUserDataSubmit} />;

      case ScanStep.SCANNING:
        return userData ? (
          <FaceScanIframe
            userData={userData}
            scanUid={scanUid}
            onScanComplete={handleScanComplete}
            onSkinScanComplete={handleSkinScanComplete}
            onError={handleScanError}
            onCancel={handleCancelScan}
          />
        ) : null;

      case ScanStep.PROCESSING:
        return (
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5">
              <Spinner
                animation="border"
                variant="primary"
                style={{ width: '4rem', height: '4rem', borderWidth: '0.35rem' }}
              />
              <h4 className="mt-4 mb-2">Analyzing Your Face</h4>
              <p className="text-muted mb-4">Our AI is processing your vital signs...</p>
              <div className="d-flex justify-content-center gap-2 flex-wrap">
                <span className="badge bg-primary-subtle text-primary p-2">Heart Rate</span>
                <span className="badge bg-success-subtle text-success p-2">Blood Oxygen</span>
                <span className="badge bg-danger-subtle text-danger p-2">Blood Pressure</span>
                <span className="badge bg-info-subtle text-info p-2">Breathing Rate</span>
                {skinScanData && (
                  <span className="badge bg-warning-subtle text-warning p-2">Skin Analysis</span>
                )}
              </div>
            </Card.Body>
          </Card>
        );

      case ScanStep.RESULTS:
        return scanResults ? (
          <ScanResults
            data={scanResults}
            userData={userData}
            onNewScan={handleNewScan}
            onDownload={handleDownloadReport}
          />
        ) : null;

      case ScanStep.ERROR:
        return (
          <Card bg="danger" text="white">
            <Card.Body>
              <h5>Scan Failed</h5>
              <p>{error || 'An unexpected error occurred. Please try again.'}</p>
              <button onClick={handleNewScan} className="btn btn-light mt-2">
                Try Again
              </button>
            </Card.Body>
          </Card>
        );

      default:
        return null;
    }
  };

  // ---------- Modal Title ----------
  const getModalTitle = () => {
    switch (currentStep) {
      case ScanStep.USER_INFO:
        return 'AI-Powered Health Assessment';
      case ScanStep.SCANNING:
        return 'Scanning...';
      case ScanStep.PROCESSING:
        return 'Processing Results';
      case ScanStep.RESULTS:
        return 'Scan Results';
      case ScanStep.ERROR:
        return 'Scan Error';
      default:
        return 'AI-Powered Health Assessment';
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size={modalSize as any}
        centered
        dialogClassName={modalDialogClass}
        backdrop={isScanActive ? 'static' : true}
        keyboard={!isScanActive}
      >
        <Modal.Header className="border-0 pb-0">
          <Modal.Title className="w-100">
            <div className="d-flex justify-content-between align-items-center">
              {currentStep === ScanStep.USER_INFO ? (
                <div className="fhp-badge">
                  <KTSVG path="/media/icons/duotune/medicine/med001.svg" className="svg-icon-5 me-1" />
                  AI-Powered Health Assessment
                </div>
              ) : (
                <h2 className="fw-bold text-dark mb-0">{getModalTitle()}</h2>
              )}
              <div
                className="btn btn-sm btn-icon btn-active-color-primary"
                onClick={isScanActive ? handleCancelScan : handleClose}
              >
                <KTSVG path="/media/icons/duotune/arrows/arr061.svg" className="svg-icon-1" />
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {isMobile ? (
            /* ===== MOBILE (Native): Full-width scan flow ===== */
            <div className="facescan-mobile-flow">
              {renderScanFlow()}
            </div>
          ) : (
            /* ===== DESKTOP (Web): Two-column layout ===== */
            <>
              {currentStep === ScanStep.USER_INFO ? (
                <div className="row g-3 facescan-entry-layout">
                  {/* Left – Hero / info panel */}
                  <div className="col-md-5 col-12">
                    <FaceScanHeroPanel />
                  </div>

                  {/* Right – QR + form */}
                  <div className="col-md-7 col-12">
                    <div className="facescan-right-panel">
                      {/* QR section */}
                      <QRScanSection qrValue={faceScanUrl} />

                      {/* OR divider */}
                      <div className="facescan-or-divider">
                        <span className="facescan-or-line" />
                        <span className="facescan-or-label">OR</span>
                        <span className="facescan-or-line" />
                      </div>

                      {/* Form header */}
                      <div className="facescan-form-header">
                        <span className="facescan-form-step-text">
                          Fill in your details to scan <strong>on this device</strong>
                        </span>
                      </div>

                      {/* Form without the banner image */}
                      <div className="facescan-form-scroll">
                        <UserDataForm onSubmit={handleUserDataSubmit} hideImage />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* When scan is active, show full-width scan flow */
                <div className="facescan-desktop-flow">
                  {renderScanFlow()}
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      <ToastContainer position="bottom-center" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
          bg="dark"
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export { FaceScanInfoModal };
